import { graphlib, layout as dagreLayout } from "@dagrejs/dagre";
import type { Edge, Node } from "@xyflow/react";

export type LayoutDirection = "TB" | "LR" | "DIAG";

// Approximate node box used for ranking/spacing. Real nodes are auto-sized by
// content, but dagre needs concrete dimensions to avoid overlaps; these match
// the editor's typical node footprint closely enough.
const NODE_W = 240;
const NODE_H = 80;

// Rough on-screen size of an edge's condition label so dagre reserves room for
// it between ranks instead of letting it overlap nodes/edges. Capped so very
// long conditions (e.g. `contains(prev.result, "VERDICT: ITERATE")`) widen the
// rank gap without exploding it.
function labelSize(label: unknown): { width: number; height: number } {
  const text = typeof label === "string" ? label : "";
  if (!text) {
    return { width: 0, height: 0 };
  }
  return { width: Math.min(240, Math.max(40, text.length * 6.4)), height: 18 };
}

// Per-rank rightward shift applied for the diagonal layout — each successive
// step advances this far to the right, turning the vertical pipeline into a
// readable down-and-right staircase. Kept modest so the cascade stays compact.
const DIAG_STEP_X = 76;

/**
 * Tidy the graph with a layered (Sugiyama) layout via dagre. Defaults to
 * "DIAG": dagre lays it out top-to-bottom (correct ordering, branches, and
 * cycle handling), then we cascade each successive rank to the right so the
 * flow reads as a clear diagonal staircase rather than a single cramped column
 * or an over-wide row. "TB"/"LR" give the plain vertical/horizontal layouts.
 *
 * Returns the same nodes with recomputed `position`s; edges are untouched.
 */
export function autoLayout(
  nodes: Node[],
  edges: Edge[],
  direction: LayoutDirection = "DIAG"
): Node[] {
  if (nodes.length === 0) {
    return nodes;
  }

  // DIAG is built on a vertical (TB) base, then offset.
  const baseDir = direction === "LR" ? "LR" : "TB";
  const lr = baseDir === "LR";
  const g = new graphlib.Graph({ multigraph: true });
  g.setGraph({
    rankdir: baseDir,
    // nodesep = gap between nodes sharing a rank; ranksep = gap between ranks.
    // Kept compact so more of the flow fits on screen; floating edge labels sit
    // on the curve midpoints rather than needing wide rank gaps.
    nodesep: lr ? 28 : 34,
    ranksep: lr ? 90 : 66,
    edgesep: 14,
    marginx: 16,
    marginy: 16,
    // "greedy" picks a better set of feedback edges to reverse for ranking,
    // which untangles cycle-heavy flows; network-simplex gives the tightest
    // ranks. Together they cut edge crossings noticeably.
    acyclicer: "greedy",
    ranker: "network-simplex",
  });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((n) => {
    g.setNode(n.id, { width: NODE_W, height: NODE_H });
  });
  edges.forEach((e) => {
    if (g.hasNode(e.source) && g.hasNode(e.target)) {
      const { width, height } = labelSize(e.label);
      // Weight the main "always" flow much heavier so dagre keeps the spine
      // short and straight; the conditional / feedback branches (lighter) are
      // pushed aside instead of cutting across it → fewer crossings.
      const isMainFlow =
        typeof e.label === "string" && e.label.trim().toLowerCase() === "always";
      // name each edge so parallel/feedback edges stay distinct (multigraph).
      g.setEdge(
        e.source,
        e.target,
        { width, height, labelpos: "c", weight: isMainFlow ? 6 : 1 },
        e.id
      );
    }
  });

  dagreLayout(g);

  const positioned = nodes.map((n) => {
    const p = g.node(n.id) as { x: number; y: number } | undefined;
    if (!p) {
      return n;
    }
    // dagre returns the node CENTER; React Flow wants the top-left corner.
    return { ...n, position: { x: p.x - NODE_W / 2, y: p.y - NODE_H / 2 } };
  });

  if (direction !== "DIAG") {
    return positioned;
  }

  // Cascade: each successive vertical rank steps further right. Rank index is
  // derived from the (quantised) y dagre assigned, so every node in a rank
  // shifts together and the spine slopes down-and-right.
  const rowYs = Array.from(
    new Set(positioned.map((n) => Math.round(n.position.y)))
  ).sort((a, b) => a - b);
  const rankOf = new Map<number, number>();
  rowYs.forEach((y, i) => rankOf.set(y, i));

  return positioned.map((n) => {
    const rank = rankOf.get(Math.round(n.position.y)) ?? 0;
    return {
      ...n,
      position: { x: n.position.x + rank * DIAG_STEP_X, y: n.position.y },
    };
  });
}

/**
 * Guess the orientation an existing layout was arranged in, from its bounding
 * box aspect ratio. Lets the editor pick matching left/right vs top/bottom node
 * handles on load so edges anchor cleanly without the user re-arranging.
 */
export function inferDirection(nodes: Node[]): LayoutDirection {
  if (nodes.length < 2) {
    return "TB";
  }
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    minX = Math.min(minX, n.position.x);
    maxX = Math.max(maxX, n.position.x);
    minY = Math.min(minY, n.position.y);
    maxY = Math.max(maxY, n.position.y);
  }
  return maxX - minX > (maxY - minY) * 1.3 ? "LR" : "TB";
}
